import { MessageCircle, Link2, Share2 } from "lucide-react";
import { t } from "./i18n";

const SITE = "https://www.indiafees.com";

export function pageUrl(path = "/", utm = "share") {
  const p = path.startsWith("/") ? path : `/${path}`;
  const sep = p.includes("?") ? "&" : "?";
  return `${SITE}${p}${sep}utm_source=whatsapp&utm_medium=${utm}`;
}

export function whatsAppShareUrl(text, url) {
  return `https://wa.me/?text=${encodeURIComponent(`${text}\n\n${url}`)}`;
}

export async function copyPageLink(url) {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    return false;
  }
}

export function ShareBar({ lang, path, title, text, messageKey, msgName, compact }) {
  const url = pageUrl(path);
  const shareText =
    text ||
    (messageKey ? t(lang, messageKey, msgName ?? title) : null) ||
    title ||
    t(lang, "shareSiteText");
  const wa = whatsAppShareUrl(shareText, url);

  if (compact) {
    return (
      <div className="if-share if-share--compact" role="group" aria-label={t(lang, "shareLabel")}>
        <a className="if-share-btn if-share-btn--wa" href={wa} target="_blank" rel="noopener noreferrer">
          <MessageCircle size={16} aria-hidden="true" />
          {t(lang, "shareWhatsApp")}
        </a>
      </div>
    );
  }
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, text: shareText, url });
        return;
      } catch { /* cancelled */ }
    }
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  const onCopy = async (e) => {
    const btn = e.currentTarget;
    const ok = await copyPageLink(url);
    if (ok) {
      const prev = btn.textContent;
      btn.textContent = t(lang, "shareCopied");
      setTimeout(() => { btn.textContent = prev; }, 2000);
    }
  };

  return (
    <div className="if-share" role="group" aria-label={t(lang, "shareLabel")}>
      <span className="if-share-label">{t(lang, "shareLabel")}</span>
      <a className="if-share-btn if-share-btn--wa" href={wa} target="_blank" rel="noopener noreferrer">
        <MessageCircle size={16} aria-hidden="true" />
        {t(lang, "shareWhatsApp")}
      </a>
      <button type="button" className="if-share-btn" onClick={onCopy}>
        <Link2 size={16} aria-hidden="true" />
        {t(lang, "shareCopy")}
      </button>
      <button type="button" className="if-share-btn if-share-btn--ghost" onClick={nativeShare}>
        <Share2 size={16} aria-hidden="true" />
        {t(lang, "shareMore")}
      </button>
    </div>
  );
}

export function ShareWhatsApp({ lang, path, messageKey, text, msgName, className = "", cardLabel }) {
  const url = pageUrl(path, "card");
  const shareText = text || (messageKey ? t(lang, messageKey, msgName) : t(lang, "shareSiteText"));
  const wa = whatsAppShareUrl(shareText, url);

  return (
    <a
      className={`if-share-wa-sm ${className}`.trim()}
      href={wa}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
    >
      <MessageCircle size={14} aria-hidden="true" />
      {t(lang, cardLabel ? "shareWhatsAppCard" : "shareWhatsApp")}
    </a>
  );
}

export function ViralBanner({ lang }) {
  const url = pageUrl("/");
  const text = t(lang, "viralBannerMsg");
  const wa = whatsAppShareUrl(text, url);

  return (
    <section className="if-viral" aria-label={t(lang, "viralBannerTitle")}>
      <div className="if-viral-inner">
        <div className="if-viral-text">
          <strong>{t(lang, "viralBannerTitle")}</strong>
          <p>{t(lang, "viralBannerBody")}</p>
        </div>
        <a className="if-viral-btn" href={wa} target="_blank" rel="noopener noreferrer">
          <MessageCircle size={20} aria-hidden="true" />
          {t(lang, "viralBannerBtn")}
        </a>
      </div>
    </section>
  );
}

export function FloatingWhatsApp({ lang, path = "/" }) {
  const url = pageUrl(path, "float");
  const text = t(lang, "shareSiteText");
  const wa = whatsAppShareUrl(text, url);

  return (
    <a
      className="if-wa-float"
      href={wa}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t(lang, "viralBannerBtn")}
    >
      <MessageCircle size={26} aria-hidden="true" />
    </a>
  );
}

/** Property tax / seasonal due-date reminder on homepage */
export function SeasonalBanner({ lang }) {
  const now = new Date();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  let key = null;
  if ((m === 3 && d >= 15) || (m === 4 && d <= 5)) key = "seasonMar";
  else if (m === 4 && d <= 30) key = "seasonApr";
  else if ((m === 9 && d >= 15) || (m === 10 && d <= 5)) key = "seasonSep";
  if (!key) return null;

  const msg = t(lang, key);
  const wa = whatsAppShareUrl(msg, pageUrl("/pay/2-chennai-property-tax-2", "season"));

  return (
    <section className="if-season" aria-label={t(lang, "seasonTitle")}>
      <p>{msg}</p>
      <a className="if-season-btn" href={wa} target="_blank" rel="noopener noreferrer">
        {t(lang, "seasonShare")}
      </a>
    </section>
  );
}
